<template>
  <div class="g-page__row p-document-selection-form">
    <h3>{{ $t('createEnvelope.documentSelectionForm.selectYourDocuments') }}</h3>
    <VGrid class="p-document-selection-form__grid" :gutter="false">
      <div slot="gridItem" type="9">
        <VSearch
          @onSearch="onSearch"
          @onResetSearch="onResetSearch"
          placeholder="Search Templates"
          data-test="contacts-search"
          et="envelope_send"
        />

        <VDocumentList
          class="p-document-selection-form__document-list"
          :loading="!$store.getters['templates/hasLoaded']"
          :list="templates"
          :loadingItems="loadingItems"
          :value="$store.getters['envelopes/templates']"
          @add="addTemplateToEnvelope"
          @remove="removeTemplateFromEnvelope"
        />

        <SElPagination
          v-if="templatesTotalPages"
          class="p-document-selection-form__paginate"
          :pager-count="5"
          @current-change="handlePageChange"
          :page-count="templatesTotalPages"
          :current-page.sync="templateData.page"
        />

      </div>
      <div slot="gridItem" type="3">
        <div class="p-document-selection-form__uploader" :class="{ 'p-document-selection-form__uploader--ie-only-height':isIE11 }">
          <VLoadingSpinner v-if="updatingUploads > 0" />
          <TWFilePicker
            class="p-document-selection-form__uploader__picker"
            multiple
            accept=".pdf,.docx,.doc,.xlsx,.xls"
            :uploaderMessage="uploaderMessageText"
            @add="addDocumentsToEnvelope"
            data-ec="envelope_send"
            data-et="upload_document_click">
            <template v-slot:files>
              <VFileList
                class="p-document-selection-form__uploader__picker__list"
                :value="uploadedDocuments"
                @remove="removeDocumentFromEnvelope"
              />
            </template>
          </TWFilePicker>
        </div>
      </div>
    </VGrid>
    <div class="g-page__row__buttons">
      <VButton
        type="cta-3"
        class="float-right"
        :action="nextStep"
        :disabled="$store.state.envelopes.creating.envelope.documents.length === 0 "
        :loading="isLoading"
        data-test="button-documents-next"
        data-ec="envelope_send"
        data-et="documents_next_step">
        <span>{{ $t('functional.nextStep') }}</span>
        <ArrowRightIcon />
      </VButton>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VButton from '@/components/deprecated/atoms/Button.vue';
import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import TWFilePicker from '@/components/tailwind/FilePicker.vue';
import VFileList from '@/components/deprecated/molecules/FileList.vue';
import VGrid from '@/components/deprecated/organisms/Grid.vue';
import VSearch from '@/components/deprecated/organisms/Search.vue';
import VDocumentList from '@/views/components/DocumentList.vue';
import { logAmplitudeEvent, logFileSizeForAmplitude, logFileTypeForAmplitude } from '@/utils/analytics';
import SElPagination from '@/views/components/SElPagination.vue';
import TemplateSearch from '@/mixins/templateSearch';
import { ArrowRightIcon } from '@/components/icons/fontIcons';

export default {
  name: 'VDocumentSelectionForm',
  description: 'List available templates and allow uploading of new documents',
  components: {
    VButton,
    VLoadingSpinner,
    VDocumentList,
    VGrid,
    VSearch,
    TWFilePicker,
    VFileList,
    SElPagination,
    ArrowRightIcon,
  },
  mixins: [TemplateSearch],
  computed: {
    ...mapGetters({
      templatesHasLoaded: 'templates/hasLoaded',
      templatesTotalPages: 'templates/totalPages',
      templatesTotalCount: 'templates/totalCount',
    }),
    isIE11() {
      return /Trident.*rv[ :]*11\./.test(navigator.userAgent);
    },
    isLoading() {
      return this.requestsInProgress > 0;
    },
    uploadedDocuments() {
      return this.$store.getters['envelopes/uploadedDocuments'];
    },
    templates() {
      return this.$store.getters['templates/pagedResults'];
    },
    uploaderMessageText() {
      return screen.width < 500 ? this.$t('functional.uploaderMessageMobile'): this.$t('functional.uploaderMessage'); // eslint-disable-line
    },
  },
  data() {
    return {
      requestsInProgress: 0,
      updatingUploads: 0,
      fingerprint: this.$route.params.envelopeFingerprint,
      loadingItems: {},
    };
  },
  mounted() {
    this.templateData.pageLimit = 9;
    this.getTemplates();
  },
  methods: {
    async addTemplateToEnvelope(document) {
      try {
        this.$set(this.loadingItems, document.fingerprint, true);
        this.requestsInProgress += 1;
        await this.$store.dispatch('envelopes/ADD_ENVELOPE_DOCUMENTS', { documents: [document] });
        logAmplitudeEvent('template-added');
        this.requestsInProgress -= 1;
        this.$set(this.loadingItems, document.fingerprint, false);
      } catch (error) {
        this.requestsInProgress -= 1;
        this.$set(this.loadingItems, document.fingerprint, false);

        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.add', { name: 'template' }),
          icon: 'times',
          type: 'error',
        }));

        if (!error.response) throw error;
      }

      return null;
    },
    async removeTemplateFromEnvelope({ fingerprint }) {
      const { id: documentId } = this.$store.getters['envelopes/documents']
        .find(doc => doc.fingerprint === fingerprint);

      if (!documentId) {
        return this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.remove', { name: 'document' }),
          icon: 'times',
          type: 'error',
        }));
      }

      try {
        this.$set(this.loadingItems, fingerprint, true);
        this.requestsInProgress += 1;
        await this.$store.dispatch('envelopes/DELETE_ENVELOPE_DOCUMENTS', { documentId });
        logAmplitudeEvent('template-removed');
        this.requestsInProgress -= 1;
        this.$set(this.loadingItems, fingerprint, false);
      } catch (error) {
        if (!error.response) throw error;

        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.remove', { name: 'template' }),
          icon: 'times',
          type: 'error',
        }));
      }

      return null;
    },
    async addDocumentsToEnvelope(files) {
      Array.from(files).forEach((file) => {
        logFileSizeForAmplitude(file);
        logFileTypeForAmplitude(file);
      });

      try {
        this.requestsInProgress += 1;
        this.updatingUploads += 1;
        await this.$store.dispatch('envelopes/ADD_ENVELOPE_DOCUMENTS', { documents: files });
        logAmplitudeEvent('document-uploaded');
        this.requestsInProgress -= 1;
        this.updatingUploads -= 1;
      } catch (error) {
        if (!error.response) throw error;

        this.requestsInProgress -= 1;
        this.updatingUploads -= 1;

        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.add', { name: 'document' }),
          icon: 'times',
          type: 'error',
        }));
      }

      return null;
    },
    async removeDocumentFromEnvelope({ fingerprint }) {
      const { id: documentId } = this.$store.getters['envelopes/documents']
        .find(doc => doc.fingerprint === fingerprint);

      if (!documentId) {
        return this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.remove', { name: 'document' }),
          icon: 'times',
          type: 'error',
        }));
      }

      try {
        this.requestsInProgress += 1;
        this.updatingUploads += 1;
        await this.$store.dispatch('envelopes/DELETE_ENVELOPE_DOCUMENTS', { documentId });
        logAmplitudeEvent('document-removed');
        this.requestsInProgress -= 1;
        this.updatingUploads -= 1;
      } catch (error) {
        if (!error.response) throw error;

        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.remove', { name: 'document' }),
          icon: 'times',
          type: 'error',
        }));
      }

      return null;
    },
    async nextStep() {
      this.requestsInProgress += 1;

      this.checkEnvelope();

      this.$emit('nextStep');

      this.requestsInProgress -= 1;
    },
    async checkEnvelope() {
      const documentsHaveBeenProcessed = this.$store.getters['envelopes/processedDocuments'];

      if (!documentsHaveBeenProcessed) {
        await this.sleep(500);

        this.$store.dispatch('envelopes/GET_ENVELOPE', {
          fingerprint: this.$route.params.envelopeFingerprint,
          isCreating: true,
        }).then(() => {
          if (!documentsHaveBeenProcessed) {
            this.checkEnvelope();
          }
        });
      }
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
  },
  // mixins: [
  //   amplitudeEventTrackMixin,
  // ],
};
</script>

<style scoped>
.p-document-selection-form__grid {
  grid-template-rows: calc(100vh - 260px);
}

.p-document-selection-form .o-search {
  padding: 15px 15px 10px 15px;
  background: white;
  border: 1px solid var(--color-border);
  border-bottom: 0;
}

.p-document-selection-form__document-list {
  padding-top: 5px !important;
  height: calc(100% - 71px);
  border-top-width: 0;
}

.p-document-selection-form__paginate {
  padding: var(--spacing-base);
  background: #fff;
  border-top: 0;
  margin-top: 10px;
  border-right: 0;
  position: relative;
}

.p-document-selection-form__uploader {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: #fff;
  height: 100%;
  position: relative;
}

.p-document-selection-form__uploader--ie-only-height {
  height: 350px;
}

.p-document-selection-form__uploader__picker {
  flex: 1;
  padding: var(--spacing-xs);
}

.p-document-selection-form__uploader__picker__list {
position: relative;
  z-index: 0;
}

/* @media screen and (max-width: var(--screen-size-small)) { */
@media screen and (max-width: 665px) {
  .p-document-selection-form__uploader {
    margin-top: 69px;
  }
}
</style>
